import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Repeat, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtDateTime, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Card } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select } from "@/components/common/Field";
import { cn } from "@/lib/utils";
import { photoUrl } from "@/components/photos/Photos";

const STEPS = ["reported", "acknowledged", "in_progress", "awaiting_part", "resolved"];
const TRANSITIONS = { reported: ["acknowledged", "in_progress", "resolved"], acknowledged: ["in_progress", "awaiting_part", "resolved"], in_progress: ["awaiting_part", "resolved"], awaiting_part: ["in_progress", "resolved"], resolved: ["in_progress"] };

export default function IssueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [issue, setIssue] = useState(null);
  const [users, setUsers] = useState([]);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  useEffect(() => { api.get("/photos", { params: { issue_id: id } }).then((r) => setPhotos(r.data)).catch(() => {}); }, [id]);
  const canWork = can("admin", "fleet_manager", "garage_supervisor");

  const load = useCallback(() => api.get(`/issues/${id}`).then((r) => setIssue(r.data)).catch(() => navigate("/issues")), [id, navigate]);
  useEffect(() => { load(); if (canWork) api.get("/users").then((r) => setUsers(r.data)); }, [load, canWork]);
  if (!issue) return <Spinner />;

  const move = async (status) => {
    try { await api.post(`/issues/${issue.id}/status`, { status }); toast.success(`Moved to ${titleCase(status)}`); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const assign = async (e) => { await api.patch(`/issues/${issue.id}`, { assigned_to: e.target.value || null }); toast.success("Assignee updated"); load(); };
  const setPriority = async (e) => { await api.patch(`/issues/${issue.id}`, { priority: e.target.value }); load(); };
  const post = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await api.post(`/issues/${issue.id}/comments`, { body: comment }); setComment(""); load();
  };
  const idx = STEPS.indexOf(issue.status);

  return (
    <div>
      <button onClick={() => navigate("/issues")} data-testid="back-to-issues" className="mb-3 flex items-center gap-1 text-sm font-medium text-primary hover:underline"><ArrowLeft className="h-4 w-4" />Issues</button>
      <PageHeader title={issue.title} testId="issue-detail-header"
        subtitle={<><Link to={`/vehicles/${issue.vehicle_id}`} className="text-primary hover:underline" data-testid="issue-vehicle-link">{issue.vehicle_name || issue.registration_no}</Link> · {issue.registration_no} · {issue.category} · reported by {issue.reported_by_name || "—"} · {fmtDateTime(issue.created_at)}</>}
        actions={<>{issue.is_recurring && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2.5 py-0.5 text-xs font-medium text-[#E65100] dark:bg-[#3D1E03] dark:text-[#FFB74D]" data-testid="recurring-fault-badge"><Repeat className="h-3 w-3" />Recurring fault</span>}<StatusBadge status={issue.priority} testId="issue-priority-badge" /><StatusBadge status={issue.status} testId="issue-status-badge" /></>} />

      <Card testId="issue-workflow" className="mb-4">
        <ol className="flex items-center gap-1 overflow-x-auto">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-1">
              <span data-testid={`issue-workflow-step-${s}`} className={cn("whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium", i < idx && "bg-secondary text-muted-foreground", i === idx && "bg-primary text-primary-foreground", i > idx && "border text-muted-foreground")}>{titleCase(s)}</span>
              {i < STEPS.length - 1 && <span className="h-px w-3 bg-border" />}
            </li>
          ))}
        </ol>
        {canWork && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="fv-caption">Move to</span>
            {TRANSITIONS[issue.status].map((s) => <button key={s} onClick={() => move(s)} data-testid={`move-to-${s}`} className="h-9 rounded-full border px-3 text-sm font-medium text-primary hover:bg-primary/10">{titleCase(s)}</button>)}
            <span className="ml-auto flex items-center gap-2">
              <Select data-testid="issue-assignee-select" className="h-9 w-auto" value={issue.assigned_to || ""} onChange={assign}><option value="">Unassigned</option>{users.filter((u) => u.role !== "operator").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select>
              <Select data-testid="issue-priority-select" className="h-9 w-auto" value={issue.priority} onChange={setPriority}>{["low", "medium", "high", "critical"].map((p) => <option key={p} value={p}>{p}</option>)}</Select>
            </span>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Activity & comments" className="lg:col-span-2" testId="issue-comments">
          {issue.description && <p className="mb-4 rounded-lg bg-secondary/50 p-3 text-sm">{issue.description}</p>}
          {photos.length > 0 && <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="issue-photos">{photos.map((p) => <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer" data-testid={`issue-photo-${p.id}`}><img src={photoUrl(p.id)} alt={p.note || "issue photo"} className="aspect-[4/3] w-full rounded-[11px] object-cover" /></a>)}</div>}
          <ul className="space-y-3">
            {issue.comments.map((c) => (
              <li key={c.id} className="text-sm" data-testid={`comment-${c.id}`}>
                {c.kind === "status" ? (
                  <p className="text-muted-foreground"><span className="font-medium text-foreground">{c.author_name}</span> moved <StatusBadge status={c.from_status} className="mx-1" /> → <StatusBadge status={c.to_status} className="mx-1" />{c.body && <span> — {c.body}</span>} <span className="text-xs">· {fmtDateTime(c.created_at)}</span></p>
                ) : (
                  <div><p className="font-medium">{c.author_name} <span className="text-xs font-normal text-muted-foreground">· {fmtDateTime(c.created_at)}</span></p><p className="mt-0.5">{c.body}</p></div>
                )}
              </li>
            ))}
            {issue.comments.length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
          </ul>
          <form onSubmit={post} className="mt-4 flex gap-2">
            <input data-testid="comment-input" className="fv-input" placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} />
            <button type="submit" data-testid="comment-submit-btn" className="flex h-10 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]" aria-label="Post comment"><Send className="h-4 w-4" /></button>
          </form>
        </Card>
        <Card title="Related (90 days, same category)" testId="related-issues">
          {issue.related_issues.length === 0 ? <p className="text-sm text-muted-foreground">No related issues.</p> : (
            <ul className="space-y-2">{issue.related_issues.map((r) => <li key={r.id}><Link to={`/issues/${r.id}`} className="flex items-center justify-between gap-2 text-sm hover:underline" data-testid={`related-issue-${r.id}`}><span className="truncate">{r.title}</span><StatusBadge status={r.status} /></Link></li>)}</ul>
          )}
          <div className="mt-4 border-t pt-3 text-sm"><p className="fv-caption">Assigned to</p><p className="mt-0.5 font-medium" data-testid="issue-assignee-name">{issue.assigned_to_name || "Unassigned"}</p></div>
        </Card>
      </div>
    </div>
  );
}
